import { useEffect, useState } from 'react';
import { CleverTap } from '@awesome-cordova-plugins/clevertap';
import type { AppState } from '@capacitor/app';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import { clearCacheMap } from 'api';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/store';
import { currentUser } from './authentication';
import { onUserLogin } from './crmUtils';
import { openInExternalUrl } from './utils';

const LOCALSTORAGE_KEY = `${import.meta.env.VITE_ENV || 'development'}_tennistalker_app_state`;

const useAppLifecycle = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate(); // Get the navigate function
  const [shouldNavigateHome, setShouldNavigateHome] = useState(false); // State to trigger navigation

  useEffect(() => {
    // cold app start
    const onDeviceReady = () => {
      if (CleverTap) {
        console.warn('CleverTap plugin found, initializing...');
        try {
          CleverTap.notifyDeviceReady();
          CleverTap.registerPush();
          if (Capacitor.getPlatform() === 'android') {
            CleverTap.createNotificationChannel('CtCS', 'Tennistalker Notifiche', 'Notifiche di Tennistalker', 1, true);
          }
          CleverTap.setDebugLevel(3);

          console.warn('CleverTap notified device ready.');
          if (currentUser()) {
            onUserLogin();
          }
        } catch (e) {
          console.error('Error initializing CleverTap:', e);
        }
      } else {
        console.warn('CleverTap plugin not available');
      }
    };

    const onNotificationClicked = (data: any) => {
      console.warn('Notification clicked:', data);
      //alertModal(`Notification clicked: ${JSON.stringify(data)}`);
      // Example: Navigate to offers page
      // history.push('/offers');
    };

    const onNotificationClickedWithExtras = (data: any) => {
      console.warn('Notification with extras:', data);
      //alertModal(`Notification with extra clicked: ${JSON.stringify(data)}`);

      if (data?.notification?.wzrk_dl) {
        // Clevertap notification with destination URL
        const destinationUrl = data.notification.wzrk_dl;
        if (destinationUrl.startsWith('/')) {
          // If it's a path, navigate to it
          navigate(destinationUrl);
        } else if (destinationUrl.startsWith('tennistalker://')) {
          // If it's a custom scheme, navigate to it
          const path = destinationUrl.replace('tennistalker://', '');
          navigate(path.startsWith('/') ? path : `/${path}`);
        } else {
          // If it's a full URL, open it in an external browser
          openInExternalUrl(destinationUrl);
        }
      }
      // Handle custom data from the notification
      if (data?.destination_url) {
        //if is a path, navigate. If is a url, open in external browser
        if (data.destination_url.startsWith('/')) {
          navigate(data.destination_url);
        } else {
          openInExternalUrl(data.destination_url);
        }
      }
    };

    if (Capacitor.isNativePlatform()) {
      // Add event listeners
      document.addEventListener('deviceready', onDeviceReady);
      document.addEventListener('onCleverTapPushNotificationClicked', onNotificationClicked);
      document.addEventListener('onPushNotification', onNotificationClickedWithExtras);
    }
    return () => {
      // Proper cleanup
      if (Capacitor.isNativePlatform()) {
        document.removeEventListener('deviceready', onDeviceReady);
        document.removeEventListener('onCleverTapPushNotificationClicked', onNotificationClicked);
        document.removeEventListener('onPushNotification', onNotificationClickedWithExtras);
      }
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleAppStateChange = (state: AppState) => {
      if (!state.isActive) {
        // app goes to background
        try {
          localStorage.setItem(LOCALSTORAGE_KEY, Date.now().toString());
        } catch (error) {
          console.error('Error in handleAppStateChange (inactive):', error);
        }
      } else {
        try {
          // resume app from background
          if (CleverTap) {
            console.warn('CleverTap plugin found, initializing...');
            CleverTap.notifyDeviceReady();
            CleverTap.registerPush();
          } else {
            console.warn('CleverTap plugin not found.');
          }
          clearCacheMap();
          const storedTime = localStorage.getItem(LOCALSTORAGE_KEY);
          if (storedTime) {
            const lastClosed = parseInt(storedTime, 10);
            const now = Date.now();
            const elapsedMinutes = (now - lastClosed) / 60000;
            console.warn(`App was closed for ${elapsedMinutes.toFixed(2)} minutes`);
            if (elapsedMinutes > 30) {
              setShouldNavigateHome(true); // Set state to trigger navigation
            }
          }
        } catch (error) {
          console.error('Error in handleAppStateChange (active):', error);
        }
      }
    };
    let resumeListener: PluginListenerHandle | null = null;

    const setupListeners = async () => {
      try {
        resumeListener = await App.addListener('appStateChange', handleAppStateChange);
      } catch (error) {
        console.error('Error setting up app state change listener:', error);
      }
    };

    if (Capacitor.isNativePlatform()) {
      setupListeners();
    }

    return () => {
      resumeListener?.remove();
    };
  }, [dispatch]);

  useEffect(() => {
    if (shouldNavigateHome) {
      navigate('/'); // Navigate to the home page
      setShouldNavigateHome(false); // Reset the state
    }
  }, [shouldNavigateHome, navigate]);

  useEffect(() => {
    const requestTrackingPermission = async () => {
      try {
        const status = await AppTrackingTransparency.requestPermission();
        console.warn('Tracking status:', status);

        if (status.status === 'authorized') {
          console.warn('Tracking authorized');
          // You can now track the user
        } else {
          console.warn('Tracking not authorized');
        }
      } catch (error) {
        console.error('Error requesting tracking permission:', error);
      }
    };

    if (Capacitor.isNativePlatform()) {
      App.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          App.exitApp(); // Close app if there's no back history
        } else {
          window.history.back();
        }
      });

      requestTrackingPermission();
    }
  }, []);
};

export default useAppLifecycle;
